import { ReactComponent as Court } from "./assets/basketball-court.svg";
import * as service from "../../service";
import React, {
  FC,
  memo,
  useCallback,
  useMemo,
  useState,
  useEffect,
  useRef,
} from "react";
import { Overlay, TimerController } from "../../components";
import {
  MESSAGES,
  TIMER_CONTINUE_EVENTS,
  TIMER_PAUSE_EVENTS,
  ICONS,
  isShotEventCode,
  isThreePointCode,
  isTwoPointCode,
} from "./constants";
import { Timer } from "./components/Timer";
import { processStatus, BASKETBALL_TABS, BasketballTabKey } from "./utils";
import { LANGUAGE } from "_constants";
import { Carousel } from "react-responsive-carousel";
import AdDisplay from "components/common/AdDisplay";
import HoopRightSvg from "./assets/right-hoop.svg";
import HoopLeftSvg from "./assets/left-hoop.svg";
import AdBannerSvg from "./assets/ad-banner.svg";
import GeniusFeeds from "../../assets/ads/genius-logo.svg";
import FastGamesApi from "../../assets/ads/fastgamesapi.svg";
import Monetria from "../../assets/ads/monetria.svg";
import { BasketballStatusRenderer } from "./components/StatusRenderer";
import { Ball } from "./components/Ball";
import { AttackIndicator } from "./indicators/AttackIndicator";
import { ThreePointsIndicator } from "./indicators/ThreePointsIndicator";
import { TwoPointsIndicator } from "./indicators/TwoPointsIndicator";
import { FreeThrowIndicator } from "./indicators/FreeThrowIndicator";
import { Stats as BasketballStats } from "./statistics";
import { ShotPath } from "./components/ShotPath";
import { PointsFlashBanner } from "./components/PointsFlashBanner";
import PreMatchIntro from "../../components/PreMatchIntro";
import BasketballMatchTimeline, {
  buildBasketballMatchTimelineEvents,
} from "./timeline";
import { SportTabs } from "components/common/SportTabs";
import { getTeamColors } from "components/colors";
import { SIDE } from "_constants";
import BasketballArena from "./assets/basketball-bg.png";
import {
  AdBanner,
  FlatLayer,
  FullHeightTabPanel,
  classNames,
  createBackgroundComponent,
} from "../../components/common";

export type BasketballProps = {
  status: service.MatchStatus;
  language?: LANGUAGE;
};

const StyledCourtBasketball = createBackgroundComponent({
  backgroundImage: BasketballArena,
  baseClassName:
    "w-full h-full bg-center bg-cover flex items-center justify-center relative overflow-hidden",
  displayName: "StyledCourtBasketball",
});

const HoopImage = React.forwardRef<
  HTMLImageElement,
  React.ImgHTMLAttributes<HTMLImageElement>
>(({ className, ...rest }, ref) => (
  <img
    ref={ref}
    alt="hoop"
    className={classNames(
      "absolute top-[31%] max-w-[10vw] h-auto z-[999]",
      className
    )}
    {...rest}
  />
));

const LeftHoop = React.forwardRef<
  HTMLImageElement,
  React.ImgHTMLAttributes<HTMLImageElement>
>((props, ref) => (
  <HoopImage
    ref={ref}
    className={classNames("left-[4%] -rotate-[4deg]", props.className)}
    {...props}
  />
));

const RightHoop = React.forwardRef<
  HTMLImageElement,
  React.ImgHTMLAttributes<HTMLImageElement>
>((props, ref) => (
  <HoopImage
    ref={ref}
    className={classNames("right-[4%] rotate-[4deg]", props.className)}
    {...props}
  />
));

const SHOT_LOCK_FAILSAFE_MS = 4500;
const SCORE_FLASH_DURATION_MS = 2000;
const SCORE_FLASH_DELAY_AFTER_SHOT_MS = 600;

type ScoreFlashState = {
  teamName: string;
  teamColor: string;
  points: number;
  label: string;
};

export const Basketball: FC<BasketballProps> = memo(
  ({ status, language = LANGUAGE.ENGLISH }) => {
    const [activeTab, setActiveTab] =
      useState<import("./utils").BasketballTabKey>("narration");

    // Points flash state (shows +1/+2/+3 after ShotPath finishes)
    const [scoreFlash, setScoreFlash] = useState<ScoreFlashState | null>(null);
    const scoreFlashShowTimeoutRef = useRef<number | null>(null);
    const scoreFlashTimerRef = useRef<number | null>(null);
    const shotLockTimeoutRef = useRef<number | null>(null);
    const shotEventCounterRef = useRef(0);
    const finalizeShotSequenceRef = useRef<() => void>(() => {});
    const [shotEventRunId, setShotEventRunId] = useState(0);
    const courtRef = useRef<HTMLDivElement | null>(null);
    const leftHoopRef = useRef<HTMLImageElement | null>(null);
    const rightHoopRef = useRef<HTMLImageElement | null>(null);
    const ballElementRef = useRef<SVGImageElement | null>(null);

    // helper: nsc -> points
    const getPointsFromNsc = (nsc?: string): number | null => {
      if (isTwoPointCode(nsc)) return 2;
      if (isThreePointCode(nsc)) return 3;
      const code = String(nsc ?? "");
      if (code === "1079") return 1;
      return null;
    };

    const [displayedStatus, setDisplayedStatus] = useState(status);
    const animatingRef = useRef(false);
    const pendingQueueRef = useRef<service.MatchStatus[]>([]);

    const applyDisplayedStatus = useCallback(
      (nextStatus: service.MatchStatus) => {
        const clonedStatus = { ...(nextStatus as any) } as service.MatchStatus;
        setDisplayedStatus(clonedStatus);
        if (isShotEventCode((nextStatus as any)?.nsc)) {
          shotEventCounterRef.current += 1;
          setShotEventRunId(shotEventCounterRef.current);
        }
      },
      []
    );

    const startProcessingStatus = useCallback(
      (nextStatus: service.MatchStatus) => {
        const incomingIsShot = isShotEventCode((nextStatus as any)?.nsc);

        if (incomingIsShot) {
          animatingRef.current = true;
          applyDisplayedStatus(nextStatus);
          if (shotLockTimeoutRef.current) {
            window.clearTimeout(shotLockTimeoutRef.current);
          }
          shotLockTimeoutRef.current = window.setTimeout(() => {
            shotLockTimeoutRef.current = null;
            finalizeShotSequenceRef.current?.();
          }, SHOT_LOCK_FAILSAFE_MS);
        } else {
          applyDisplayedStatus(nextStatus);
        }
      },
      [applyDisplayedStatus]
    );

    const drainPendingQueue = useCallback(() => {
      while (pendingQueueRef.current.length > 0) {
        const nextStatus = pendingQueueRef.current.shift();
        if (!nextStatus) continue;
        const nextIsShot = isShotEventCode((nextStatus as any)?.nsc);
        startProcessingStatus(nextStatus);
        if (nextIsShot) {
          return;
        }
      }
    }, [startProcessingStatus]);

    const finalizeShotSequence = useCallback(() => {
      if (scoreFlashShowTimeoutRef.current) {
        window.clearTimeout(scoreFlashShowTimeoutRef.current);
        scoreFlashShowTimeoutRef.current = null;
      }
      if (scoreFlashTimerRef.current) {
        window.clearTimeout(scoreFlashTimerRef.current);
        scoreFlashTimerRef.current = null;
      }
      if (shotLockTimeoutRef.current) {
        window.clearTimeout(shotLockTimeoutRef.current);
        shotLockTimeoutRef.current = null;
      }
      setScoreFlash(null);
      animatingRef.current = false;
      drainPendingQueue();
    }, [drainPendingQueue]);

    useEffect(() => {
      finalizeShotSequenceRef.current = finalizeShotSequence;
      return () => {
        finalizeShotSequenceRef.current = () => {};
      };
    }, [finalizeShotSequence]);

    // cleanup on unmount
    useEffect(() => {
      return () => {
        if (scoreFlashTimerRef.current) {
          window.clearTimeout(scoreFlashTimerRef.current);
          scoreFlashTimerRef.current = null;
        }
        if (scoreFlashShowTimeoutRef.current) {
          window.clearTimeout(scoreFlashShowTimeoutRef.current);
          scoreFlashShowTimeoutRef.current = null;
        }
        if (shotLockTimeoutRef.current) {
          window.clearTimeout(shotLockTimeoutRef.current);
          shotLockTimeoutRef.current = null;
        }
        pendingQueueRef.current = [];
      };
    }, []);

    // When external status updates, either queue it (if animating) or show immediately
    useEffect(() => {
      if (animatingRef.current) {
        pendingQueueRef.current.push({
          ...(status as any),
        } as service.MatchStatus);
        return;
      }

      startProcessingStatus({ ...(status as any) } as service.MatchStatus);
    }, [startProcessingStatus, status]);

    const { normalizedStatusCode } = useMemo(
      () => processStatus(displayedStatus, language),
      [displayedStatus, language]
    );

    const nscStr = useMemo(
      () => String((displayedStatus as any)?.nsc ?? ""),
      [displayedStatus]
    );

    const showShotPath = isShotEventCode(nscStr); // show for 2P, 3P, and FT Scored only
    useEffect(() => {
      if (!isShotEventCode((displayedStatus as any)?.nsc)) {
        shotEventCounterRef.current += 1;
        setShotEventRunId(shotEventCounterRef.current);
      }
    }, [displayedStatus]);

    const messageGenerator = useCallback(
      ({
        language,
        normalizedStatusCode,
        statusMessage,
      }: {
        language: LANGUAGE;
        normalizedStatusCode?: string;
        statusMessage?: string;
      }) => {
        if (!normalizedStatusCode) return undefined;
        const langDict = MESSAGES[language];
        if (!langDict) return undefined;

        let message =
          normalizedStatusCode === "1280"
            ? statusMessage ?? ""
            : langDict[normalizedStatusCode] ?? "";

        const Icon = ICONS[normalizedStatusCode];

        return { message: message?.toUpperCase(), icon: Icon };
      },
      []
    );

    const isPreMatch = useMemo(() => {
      const stsVal = displayedStatus?.sts as unknown;
      const stsNum =
        typeof stsVal === "number"
          ? stsVal
          : typeof stsVal === "string"
          ? Number(stsVal)
          : NaN;

      const notStartedByStatus = !Number.isNaN(stsNum) && stsNum === 0;

      const tmVal = displayedStatus?.tm as unknown;
      const tmNum =
        typeof tmVal === "number"
          ? tmVal
          : typeof tmVal === "string"
          ? Number(tmVal)
          : NaN;

      const notStartedByTime = !Number.isNaN(tmNum) && tmNum <= 0;

      const noLiveNsc = !displayedStatus?.nsc;
      return (notStartedByStatus || notStartedByTime) && noLiveNsc;
    }, [displayedStatus]);

    const matchEvents = useMemo(
      () => buildBasketballMatchTimelineEvents(status),
      [status]
    );
    const hasMatchTimeline = useMemo(
      () =>
        matchEvents.some(
          (event) => (event.minute ?? 0) > 0 || event.team != null
        ),
      [matchEvents]
    );

    const tabs = useMemo(
      () =>
        BASKETBALL_TABS.filter(
          (tab) => tab.key !== "match" || hasMatchTimeline
        ),
      [hasMatchTimeline]
    );

    useEffect(() => {
      if (!tabs.some((tab) => tab.key === activeTab) && tabs.length > 0) {
        setActiveTab(tabs[0].key);
      }
    }, [tabs, activeTab]);

    const selectedTabIndex = useMemo(() => {
      const idx = tabs.findIndex((tab) => tab.key === activeTab);
      return idx >= 0 ? idx : 0;
    }, [tabs, activeTab]);

    const homeTeamName = useMemo(() => {
      const s = displayedStatus as any;
      return (
        s?.T1?.n ??
        s?.t1?.n ??
        s?.T1n ??
        s?.t1n ??
        s?.T1?.name ??
        s?.t1?.name ??
        ""
      );
    }, [displayedStatus]);

    const awayTeamName = useMemo(() => {
      const s = displayedStatus as any;
      return (
        s?.T2?.n ??
        s?.t2?.n ??
        s?.T2n ??
        s?.t2n ??
        s?.T2?.name ??
        s?.t2?.name ??
        ""
      );
    }, [displayedStatus]);

    return (
      <>
        <SportTabs
          tabs={tabs}
          activeKey={activeTab}
          onChange={(key) => setActiveTab(key as BasketballTabKey)}
        />

        <Carousel
          showThumbs={false}
          selectedItem={selectedTabIndex}
          autoPlay={false}
          emulateTouch
          showStatus={false}
          showArrows={false}
          showIndicators={false}
          swipeable={false}
        >
          {tabs.map((tab) => {
            if (tab.key === "narration") {
              return (
                <StyledCourtBasketball
                  className="styled-court"
                  key="narration"
                  ref={courtRef}
                >
                  <Timer status={displayedStatus} />
                  <AdBanner src={AdBannerSvg} alt="Ad Banner" />
                  <AdDisplay adImages={[GeniusFeeds, FastGamesApi, Monetria]} />
                  <LeftHoop
                    src={HoopLeftSvg}
                    alt="Left Hoop"
                    ref={leftHoopRef}
                  />

                  {!isPreMatch && showShotPath && (
                    <ShotPath
                      key={shotEventRunId}
                      status={displayedStatus}
                      onStart={() => {
                        animatingRef.current = true;
                      }}
                      onEnd={() => {
                        const pts = getPointsFromNsc(
                          (displayedStatus as any)?.nsc
                        );

                        if (scoreFlashShowTimeoutRef.current) {
                          window.clearTimeout(scoreFlashShowTimeoutRef.current);
                          scoreFlashShowTimeoutRef.current = null;
                        }
                        if (scoreFlashTimerRef.current) {
                          window.clearTimeout(scoreFlashTimerRef.current);
                          scoreFlashTimerRef.current = null;
                        }

                        if (pts != null) {
                          const sideValue = (displayedStatus as any)?.side;
                          const normalizedSide = String(sideValue);
                          const { home, away } = getTeamColors(
                            displayedStatus as any
                          );
                          const teamColor =
                            normalizedSide === String(SIDE.HOME)
                              ? home
                              : normalizedSide === String(SIDE.AWAY)
                              ? away
                              : "#ffffff";

                          const teamName =
                            normalizedSide === String(SIDE.HOME)
                              ? homeTeamName
                              : normalizedSide === String(SIDE.AWAY)
                              ? awayTeamName
                              : "";

                          const label =
                            pts === 1
                              ? "1 POINT"
                              : `${pts} POINT${pts > 1 ? "S" : ""}`;

                          const nextFlash: ScoreFlashState = {
                            teamName,
                            teamColor,
                            points: pts,
                            label,
                          };

                          scoreFlashShowTimeoutRef.current = window.setTimeout(
                            () => {
                              scoreFlashShowTimeoutRef.current = null;
                              setScoreFlash(nextFlash);

                              scoreFlashTimerRef.current = window.setTimeout(
                                () => {
                                  finalizeShotSequence();
                                },
                                SCORE_FLASH_DURATION_MS
                              );
                            },
                            SCORE_FLASH_DELAY_AFTER_SHOT_MS
                          );
                        } else {
                          finalizeShotSequence();
                        }

                        // Clear shot lock fail-safe; actual unlock happens when flash hides (or immediately above for non-scoring events)
                        if (shotLockTimeoutRef.current) {
                          window.clearTimeout(shotLockTimeoutRef.current);
                          shotLockTimeoutRef.current = null;
                        }
                      }}
                      courtRef={courtRef}
                      leftHoopRef={leftHoopRef}
                      rightHoopRef={rightHoopRef}
                      ballRef={ballElementRef}
                      eventRunId={shotEventRunId}
                    />
                  )}

                  <Overlay
                    status={displayedStatus}
                    messageGenerator={messageGenerator}
                    language={language}
                    StatusRenderer={BasketballStatusRenderer}
                  >
                    <Court />

                    {!isPreMatch && (
                      <>
                        <AttackIndicator status={displayedStatus} />
                        <TwoPointsIndicator status={displayedStatus} />
                        <ThreePointsIndicator status={displayedStatus} />
                        <FreeThrowIndicator status={displayedStatus} />
                        <Ball
                          status={displayedStatus}
                          ballElementRef={ballElementRef}
                        />
                      </>
                    )}
                  </Overlay>

                  {scoreFlash && (
                    <PointsFlashBanner
                      teamName={scoreFlash.teamName}
                      points={scoreFlash.points}
                      teamColor={scoreFlash.teamColor}
                      label={scoreFlash.label}
                    />
                  )}

                  {isPreMatch && (
                    <FlatLayer>
                      <PreMatchIntro status={displayedStatus} />
                    </FlatLayer>
                  )}

                  <RightHoop
                    src={HoopRightSvg}
                    alt="Right Hoop"
                    ref={rightHoopRef}
                  />
                </StyledCourtBasketball>
              );
            }

            if (tab.key === "stats") {
              return (
                <FullHeightTabPanel key="stats">
                  <BasketballStats status={status} language={language} />
                </FullHeightTabPanel>
              );
            }

            if (tab.key === "match") {
              return (
                <FullHeightTabPanel key="match">
                  <BasketballMatchTimeline
                    events={matchEvents}
                    status={status}
                  />
                </FullHeightTabPanel>
              );
            }

            return null;
          })}
        </Carousel>
        <TimerController
          event={normalizedStatusCode}
          eventsContinue={TIMER_CONTINUE_EVENTS}
          eventsPause={TIMER_PAUSE_EVENTS}
        />
      </>
    );
  }
);

export { StyledCourtBasketball, Overlay };
